import { requireValue } from '../../../shared/require_value.js';
import type { ReactElement } from 'react';
import type { ChatUiFlightSearchConfirmation } from '@voll-intelligence/genui-contract';
import {
  AirplaneTakeoffIcon,
  ArrowRightIcon,
  ArrowsLeftRightIcon,
  CalendarBlankIcon,
  MagnifyingGlassIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { AirplaneSeatIcon } from '@voll-intelligence/ui-kit/icons/airplane-seat';
import { fmtWeekdayDate } from '../../../shared/ui/flight_format.js';
import { useGenUiI18n, type GenUiKey } from '../../../shared/ui/i18n.js';
import './flight_search_confirmation.css';

const CABIN_KEYS: Record<string, GenUiKey> = {
  economy: 'flight.search.cabin.economy',
  premium: 'flight.search.cabin.premium',
  business: 'flight.search.cabin.business',
  first: 'flight.search.cabin.first',
};

/** Dias entre duas datas ISO (>= 0). */
function daysBetween(a: string, b: string): number {
  const pa = /^(\d{4})-(\d{2})-(\d{2})/.exec(a);
  const pb = /^(\d{4})-(\d{2})-(\d{2})/.exec(b);
  if (!pa || !pb) return 0;
  const da = Date.UTC(Number(pa[1]), Number(pa[2]) - 1, Number(pa[3]));
  const db = Date.UTC(Number(pb[1]), Number(pb[2]) - 1, Number(pb[3]));
  return Math.max(0, Math.round((db - da) / 86_400_000));
}

type Pax = ChatUiFlightSearchConfirmation['passengers'];

/** Título: total de passageiros — "1 passageiro" / "3 passageiros". */
function paxCount(p: Pax, t: (key: GenUiKey, values?: Record<string, number>) => string): string {
  const n = p.adults + (p.children ?? 0) + (p.infants ?? 0);
  return t(n === 1 ? 'flight.search.passenger_one' : 'flight.search.passenger_other', {
    count: n,
  });
}

/** Rótulo: composição — 1 pax → "Adulto"/"Criança"/"Bebê"; senão "2 adultos · 1 criança". */
function paxComposition(
  p: Pax,
  t: (key: GenUiKey, values?: Record<string, number>) => string,
): string {
  const n = p.adults + (p.children ?? 0) + (p.infants ?? 0);
  if (n === 1) {
    if (p.adults) return t('flight.search.adult');
    if (p.children) return t('flight.search.child');
    return t('flight.search.infant');
  }
  const parts: string[] = [];
  if (p.adults)
    parts.push(
      t(p.adults === 1 ? 'flight.search.adult_one' : 'flight.search.adult_other', {
        count: p.adults,
      }),
    );
  if (p.children)
    parts.push(
      t(p.children === 1 ? 'flight.search.child_one' : 'flight.search.child_other', {
        count: p.children,
      }),
    );
  if (p.infants)
    parts.push(
      t(p.infants === 1 ? 'flight.search.infant_one' : 'flight.search.infant_other', {
        count: p.infants,
      }),
    );
  return parts.join(' · ');
}

/**
 * Resumo pré-busca de voo (`flight_search_confirmation`, handoff "Flight search
 * confirmation card"): a fala do assistente acima + um card com rota
 * (IATA + cidade), resumo da viagem (tipo · datas · passageiros · classe) e a
 * ação "Buscar voos". Display-only fora o botão — quando `onSearch` é fornecido
 * (turno ativo), o clique dispara a busca; sem ele (histórico/resolvido) o card
 * fica só leitura. Projeção determinística dos args da tool `confirm_flight_search`.
 */
export function FlightSearchConfirmationCard({
  search,
  note,
  onSearch,
}: {
  readonly search: ChatUiFlightSearchConfirmation;
  readonly note?: string;
  readonly onSearch?: () => void;
}) {
  const { t, locale } = useGenUiI18n();
  const roundTrip = Boolean(search.returnDate);
  const cabin = t(CABIN_KEYS[search.cabin ?? 'economy'] ?? 'flight.search.cabin.economy');
  const days = roundTrip ? daysBetween(search.departureDate, requireValue(search.returnDate)) : 0;
  const datesTitle = roundTrip
    ? `${fmtWeekdayDate(search.departureDate, locale)} — ${fmtWeekdayDate(
        requireValue(search.returnDate),
        locale,
      )}`
    : fmtWeekdayDate(search.departureDate, locale);
  const dayKey = days === 1 ? 'flight.search.day_one' : 'flight.search.day_other';
  const datesLabel = roundTrip ? t(dayKey, { count: days }) : t('flight.quote.outbound');

  return (
    <section className="fsc" aria-label={t('flight.search.aria')}>
      <div className="fsc-card">
        <div className="fsc-route">
          <div className="fsc-rpt">
            <span className="fsc-iata">{search.origin}</span>
            {search.originLabel && <span className="fsc-city">{search.originLabel}</span>}
          </div>
          <div className="fsc-rmid" aria-hidden="true">
            <span className="fsc-rline" />
            <AirplaneTakeoffIcon size={18} aria-hidden />
            <span className="fsc-rline" />
          </div>
          <div className="fsc-rpt fsc-rpt-end">
            <span className="fsc-iata">{search.destination}</span>
            {search.destinationLabel && <span className="fsc-city">{search.destinationLabel}</span>}
          </div>
        </div>

        <div className="fsc-divider" />

        <div className="fsc-summary">
          <SummaryRow
            icon={
              roundTrip ? (
                <ArrowsLeftRightIcon size={20} aria-hidden />
              ) : (
                <ArrowRightIcon size={20} aria-hidden />
              )
            }
            title={roundTrip ? t('flight.search.roundTrip') : t('flight.search.oneWay')}
            label={t('flight.search.tripType')}
          />
          <SummaryRow
            icon={<CalendarBlankIcon size={20} aria-hidden />}
            title={datesTitle}
            label={datesLabel}
          />
          <SummaryRow
            icon={<UsersIcon size={20} aria-hidden />}
            title={paxCount(search.passengers, t)}
            label={paxComposition(search.passengers, t)}
          />
          <SummaryRow
            icon={<AirplaneSeatIcon size={20} aria-hidden />}
            title={cabin}
            label={t('flight.search.class')}
          />
        </div>

        {note && <p className="fsc-note">{note}</p>}

        {onSearch && (
          <button type="button" className="fsc-search" onClick={onSearch}>
            <MagnifyingGlassIcon size={18} aria-hidden />
            {t('flight.search.searchFlights')}
          </button>
        )}
      </div>
    </section>
  );
}

function SummaryRow({
  icon,
  title,
  label,
}: {
  readonly icon: ReactElement;
  readonly title: string;
  readonly label: string;
}) {
  return (
    <div className="fsc-row">
      <span className="fsc-row-ic">{icon}</span>
      <span className="fsc-row-txt">
        <span className="fsc-row-title">{title}</span>
        <span className="fsc-row-label">{label}</span>
      </span>
    </div>
  );
}
